import type { MutateOptions, Query } from '@tanstack/react-query'
import { useEffect, useRef } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'

import { SessionChangedError } from '@scrambl/shared/session-scope'

export interface OwnedReaderSession<Context> {
  key: unknown
  assertCurrent(): void
  run<T>(
    signal: AbortSignal,
    action: (context: Context) => Promise<T>,
  ): Promise<T>
}
type Command<Input, Context> = {
  input: Input
  session: OwnedReaderSession<Context>
}
export interface OwnedReaderMutationOptions<Input, Result, Context> {
  execute: (input: Input, context: Context) => Promise<Result>
  queryRoot: readonly unknown[]
  readerPredicate?: (query: Query, input: Input, result?: Result) => boolean
  singleFlight?: boolean
  busyMessage?: string
  successMessage?: string | ((result: Result) => string)
  errorMessage?: string
  successTone?: (result: Result) => 'success' | 'warning'
  extraReaders?: (input: Input) => readonly (readonly unknown[])[]
}
/** Freeze inputs and capture a reader lease; mutations never seed server bodies. */
export function useOwnedReaderMutation<Input, Result, Context>(
  session: OwnedReaderSession<Context>,
  options: OwnedReaderMutationOptions<Input, Result, Context>,
) {
  const client = useQueryClient()
  const mounted = useRef(false)
  const inFlight = useRef<{ key: string; token: symbol } | null>(null)
  const currentKey = useRef('')
  currentKey.current = JSON.stringify(session.key)
  useEffect(() => {
    mounted.current = true
    return () => {
      mounted.current = false
    }
  }, [])
  const isCurrent = (command: Command<Input, Context>, ui = false) => {
    try {
      command.session.assertCurrent()
    } catch {
      return false
    }
    return (
      !ui ||
      (mounted.current &&
        currentKey.current === JSON.stringify(command.session.key))
    )
  }
  const invalidate = (command: Command<Input, Context>, result?: Result) => {
    if (!isCurrent(command)) return
    void client.invalidateQueries({
      queryKey: options.queryRoot,
      predicate: query =>
        JSON.stringify(query.queryKey.at(-1)) ===
          JSON.stringify(command.session.key) &&
        (options.readerPredicate?.(query, command.input, result) ?? true),
    })
    for (const key of options.extraReaders?.(command.input) ?? [])
      void client.invalidateQueries({ queryKey: key })
  }
  const mutation = useMutation<Result, Error, Command<Input, Context>>({
    networkMode: 'always',
    retry: false,
    meta: { silent: true },
    mutationFn: command =>
      command.session.run(new AbortController().signal, context =>
        options.execute(command.input, context),
      ),
    onSuccess: (result, command) => {
      invalidate(command, result)
      if (options.successMessage && isCurrent(command, true))
        (options.successTone?.(result) === 'warning'
          ? toast.warning
          : toast.success)(
          typeof options.successMessage === 'function'
            ? options.successMessage(result)
            : options.successMessage,
        )
    },
    onError: (_error, command) => {
      invalidate(command)
      if (isCurrent(command, true))
        toast.error(
          options.errorMessage ??
            'The change could not be confirmed. Refresh before trying again.',
        )
    },
  })
  type Callbacks = MutateOptions<Result, Error, Input, unknown>
  const bind = (
    callbacks?: Callbacks,
  ): MutateOptions<Result, Error, Command<Input, Context>, unknown> => ({
    onSuccess: (result, command, context, info) => {
      if (isCurrent(command, true))
        callbacks?.onSuccess?.(result, command.input, context, info)
    },
    onError: (error, command, context, info) => {
      if (isCurrent(command, true))
        callbacks?.onError?.(error, command.input, context, info)
    },
    onSettled: (result, error, command, context, info) => {
      if (isCurrent(command, true))
        callbacks?.onSettled?.(result, error, command.input, context, info)
    },
  })
  const run = async (input: Input, callbacks?: Callbacks) => {
    const flight = { key: JSON.stringify(session.key), token: Symbol() }
    if (options.singleFlight && inFlight.current?.key === flight.key)
      throw new Error(options.busyMessage ?? 'Change is already in progress')
    if (options.singleFlight) inFlight.current = flight
    try {
      const command = { input: structuredClone(input), session }
      const result = await mutation.mutateAsync(command, bind(callbacks))
      if (!isCurrent(command, true)) throw new SessionChangedError()
      return result
    } finally {
      if (inFlight.current?.token === flight.token) inFlight.current = null
    }
  }
  const visible = !mutation.variables || isCurrent(mutation.variables)
  return {
    ...mutation,
    data: visible ? mutation.data : undefined,
    error: visible ? mutation.error : null,
    isPending: visible && mutation.isPending,
    isSuccess: visible && mutation.isSuccess,
    isError: visible && mutation.isError,
    isIdle: !visible || mutation.isIdle,
    status: visible ? mutation.status : ('idle' as const),
    context: visible ? mutation.context : undefined,
    failureReason: visible ? mutation.failureReason : null,
    failureCount: visible ? mutation.failureCount : 0,
    isPaused: visible && mutation.isPaused,
    submittedAt: visible ? mutation.submittedAt : 0,
    variables: visible ? mutation.variables?.input : undefined,
    mutate: (input: Input, callbacks?: Callbacks) => {
      if (options.singleFlight) {
        void run(input, callbacks).catch(() => {})
        return
      }
      mutation.mutate(
        { input: structuredClone(input), session },
        bind(callbacks),
      )
    },
    mutateAsync: run,
  }
}
